import { useState } from 'react';

import jsPDF from 'jspdf';

import { useGetDateFNSLocale } from '@/locales/helpers';
import { useProceduresBySpecialty } from '@/react-query/useReferralInfo';
import { useReferralPdfInfo } from '@/react-query/useReferralPdfInfo';
import { useUniversalToFDI } from '@/utils/internationalization/useUniversalToFDI';
import { parseAndFormatDate } from '@/utils/parseDate';

interface ReferralPdfGeneratorProps {
  // Can use either unifiedAppointmentId or referralId to fetch from API
  unifiedAppointmentId?: string;
  referralId?: string;
  appointment?: any;
}

export const useReferralPdfGenerator = ({
  unifiedAppointmentId,
  referralId,
  appointment,
}: ReferralPdfGeneratorProps) => {
  const dateFNSLocale = useGetDateFNSLocale();
  const { displayToothNumber } = useUniversalToFDI();
  const [isGenerating, setIsGenerating] = useState(false);

  // Prefer referralId over unifiedAppointmentId if both are provided
  const identifier = referralId || unifiedAppointmentId || '';
  const useReferralId = !!referralId;

  // Fetch PDF info from API
  const { data: pdfData, isLoading: isLoadingPdfInfo } = useReferralPdfInfo(
    identifier,
    { enabled: !!identifier, useReferralId },
  );

  const specialtyId = pdfData?.specialtyId;

  // Get procedures data for display
  const { data: procedures } = useProceduresBySpecialty(specialtyId || 0, {
    enabled: !!specialtyId,
  });

  const generatePdf = async () => {
    if (!pdfData) {
      console.error('No data available for PDF generation');
      return;
    }

    setIsGenerating(true);

    try {
      const patientName = `${pdfData.patientFirstName || ''} ${
        pdfData.patientLastName || ''
      }`.trim();
      const fileName = patientName
        ? `referral-${patientName.toLowerCase().replace(/\s+/g, '-')}`
        : 'referral-form';

      // Create new PDF document
      const pdf = new jsPDF('p', 'pt', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 40;
      const contentWidth = pageWidth - margin * 2;

      let yPosition = margin;
      let currentPage = 1;

      // Helper function to check if we need a new page
      const checkPageBreak = (requiredHeight: number) => {
        if (yPosition + requiredHeight > pageHeight - margin) {
          pdf.addPage();
          yPosition = margin;
          currentPage++;
          return true;
        }
        return false;
      };

      // Helper function to add text with word wrapping and return height used
      const addWrappedText = (
        text: string,
        x: number,
        y: number,
        maxWidth: number,
        fontSize: number = 12,
        lineHeight: number = 1.2,
      ) => {
        if (!text) return 0;

        pdf.setFontSize(fontSize);
        const lines = pdf.splitTextToSize(text, maxWidth);
        const height = lines.length * fontSize * lineHeight;

        pdf.text(lines, x, y);
        return height;
      };

      // Helper function to add section header with dynamic spacing
      const addSectionHeader = (text: string, y: number) => {
        const headerHeight = 30;
        checkPageBreak(headerHeight);

        pdf.setFontSize(16);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(31, 41, 55); // #1f2937
        pdf.text(text, margin, y);

        // Add underline
        pdf.setDrawColor(229, 231, 235); // #e5e7eb
        pdf.setLineWidth(2);
        pdf.line(margin, y + 5, margin + 200, y + 5);

        yPosition = y + headerHeight;
        return yPosition;
      };

      // Helper function to add field with dynamic height calculation
      const addField = (
        label: string,
        value: string,
        x: number,
        y: number,
        fieldWidth: number,
        fontSize: number = 12,
      ) => {
        if (!label && !value) return 0;

        const labelHeight = fontSize * 1.2;
        const labelWidth = pdf.getTextWidth(label);
        const valueX = x + labelWidth + 15;
        const valueWidth = fieldWidth - labelWidth - 15;

        // Add label (bold)
        pdf.setFontSize(fontSize);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(31, 41, 55);
        pdf.text(label, x, y);

        // Add value (normal weight)
        pdf.setFontSize(fontSize);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(31, 41, 55);
        const valueHeight = addWrappedText(
          value || '-',
          valueX,
          y,
          valueWidth,
          fontSize,
        );

        // Return the maximum height used
        return Math.max(labelHeight, valueHeight);
      };

      // Helper function to add image to PDF
      const addImageToPdf = async (
        imageUrl: string,
        x: number,
        y: number,
        maxWidth: number,
        maxHeight: number,
      ) => {
        try {
          // Create a canvas to load and resize the image
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          const img = new Image();

          return new Promise<number>((resolve) => {
            img.onload = () => {
              // Calculate aspect ratio to maintain proportions
              const aspectRatio = img.width / img.height;
              let width = maxWidth;
              let height = width / aspectRatio;

              if (height > maxHeight) {
                height = maxHeight;
                width = height * aspectRatio;
              }

              canvas.width = width;
              canvas.height = height;

              if (ctx) {
                ctx.drawImage(img, 0, 0, width, height);

                // Convert canvas to data URL and add to PDF
                const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
                pdf.addImage(dataUrl, 'JPEG', x, y, width, height);
              }

              resolve(height);
            };

            img.onerror = () => {
              // If image fails to load, add a placeholder text
              pdf.setFontSize(10);
              pdf.setTextColor(102, 102, 102);
              pdf.text('Image not available', x, y + 20);
              resolve(30);
            };

            img.crossOrigin = 'anonymous';
            img.src = imageUrl;
          });
        } catch (error) {
          // Fallback if image loading fails
          pdf.setFontSize(10);
          pdf.setTextColor(102, 102, 102);
          pdf.text('Image not available', x, y + 20);
          return 30;
        }
      };

      // Header
      pdf.setFontSize(24);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(37, 99, 235); // #2563eb
      const titleWidth = pdf.getTextWidth('Dental Referral Form');
      pdf.text('Dental Referral Form', (pageWidth - titleWidth) / 2, yPosition);

      yPosition += 30;

      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(102, 102, 102); // #666
      const dateWidth = pdf.getTextWidth(new Date().toLocaleDateString());
      pdf.text(
        new Date().toLocaleDateString(),
        (pageWidth - dateWidth) / 2,
        yPosition,
      );

      yPosition += 40;

      // Format date of birth
      const formatDateOfBirth = () => {
        if (pdfData.patientDateOfBirth) {
          return parseAndFormatDate(
            pdfData.patientDateOfBirth,
            'd MMMM yyyy',
            dateFNSLocale,
          );
        }
        return '';
      };

      // Get selected teeth display
      const getSelectedTeethDisplay = () => {
        if (!pdfData.toothNumbers || pdfData.toothNumbers.length === 0)
          return '';

        return pdfData.toothNumbers
          .map((toothNumber) => displayToothNumber(toothNumber))
          .join(', ');
      };

      // Get selected treatments display
      const getSelectedTreatmentsDisplay = () => {
        if (!pdfData.proceduresIds || pdfData.proceduresIds.length === 0)
          return '';
        if (!procedures) return '';

        return pdfData.proceduresIds
          .map((treatmentId: number) => {
            const procedure = procedures.find(
              (p) => p.procedure_id === treatmentId,
            );
            return procedure?.procedure_name || '';
          })
          .filter(Boolean)
          .join(', ');
      };

      // Format appointment date
      const formatAppointmentDate = () => {
        if (!appointment?.appointment?.startTime) return '-';

        try {
          const appointmentDate = new Date(appointment.appointment.startTime);
          return appointmentDate.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          });
        } catch (error) {
          return '-';
        }
      };

      // Patient Information Section
      yPosition = addSectionHeader('Patient Information', yPosition);

      const fieldWidth = contentWidth / 2 - 10;
      const fieldSpacing = 20;

      // Calculate heights for all fields in this section
      const nameHeight = addField(
        'Name:',
        `${pdfData.patientFirstName || ''} ${pdfData.patientLastName || ''}`,
        margin,
        yPosition,
        fieldWidth,
      );
      const dobHeight = addField(
        'Date of Birth:',
        formatDateOfBirth(),
        margin + fieldWidth + 20,
        yPosition,
        fieldWidth,
      );

      yPosition += Math.max(nameHeight, dobHeight) + fieldSpacing;
      checkPageBreak(50); // Reserve space for next row

      const emailHeight = addField(
        'Email:',
        pdfData.patientEmail || '',
        margin,
        yPosition,
        fieldWidth,
      );
      const phoneHeight = addField(
        'Phone:',
        pdfData.patientPhone || '',
        margin + fieldWidth + 20,
        yPosition,
        fieldWidth,
      );

      yPosition += Math.max(emailHeight, phoneHeight) + fieldSpacing;

      // Referring Doctor Information
      yPosition = addSectionHeader('Referring Doctor Information', yPosition);

      const doctorNameHeight = addField(
        'Doctor Name:',
        `${pdfData.doctorFirstName || ''} ${pdfData.doctorLastName || ''}`,
        margin,
        yPosition,
        fieldWidth,
      );
      const practicePhoneHeight = addField(
        'Practice Phone:',
        pdfData.doctorPhone || '',
        margin + fieldWidth + 20,
        yPosition,
        fieldWidth,
      );

      yPosition +=
        Math.max(doctorNameHeight, practicePhoneHeight) + fieldSpacing;
      checkPageBreak(50);

      const practiceEmailHeight = addField(
        'Practice Email:',
        pdfData.practiceEmail || '',
        margin,
        yPosition,
        fieldWidth,
      );

      yPosition += practiceEmailHeight + fieldSpacing;

      // Treating Specialist Information
      yPosition = addSectionHeader(
        'Treating Specialist Information',
        yPosition,
      );

      const practiceNameHeight = addField(
        'Practice Name:',
        pdfData.referralPracticeName || '',
        margin,
        yPosition,
        fieldWidth,
      );
      const specialistPhoneHeight = addField(
        'Practice Phone:',
        pdfData.referralPracticePhone || '',
        margin + fieldWidth + 20,
        yPosition,
        fieldWidth,
      );

      yPosition +=
        Math.max(practiceNameHeight, specialistPhoneHeight) + fieldSpacing;
      checkPageBreak(50);

      const practiceAddressHeight = addField(
        'Practice Address:',
        pdfData.referralPracticeAddress || '',
        margin,
        yPosition,
        fieldWidth,
      );

      yPosition += practiceAddressHeight + fieldSpacing;

      // Treatment Information
      yPosition = addSectionHeader('Treatment Information', yPosition);

      const teethHeight = addField(
        'Selected Teeth:',
        getSelectedTeethDisplay(),
        margin,
        yPosition,
        contentWidth,
      );
      yPosition += teethHeight + fieldSpacing;
      checkPageBreak(50);

      const proceduresHeight = addField(
        'Procedures:',
        getSelectedTreatmentsDisplay(),
        margin,
        yPosition,
        contentWidth,
      );
      yPosition += proceduresHeight + fieldSpacing;

      // Notes Section - Dynamic sizing based on content length
      if (pdfData.narrative) {
        const notesText = pdfData.narrative || '';
        const estimatedNotesHeight = Math.ceil(notesText.length / 80) * 16; // Rough estimate

        checkPageBreak(estimatedNotesHeight + 50); // Reserve space for header + content

        yPosition = addSectionHeader('Notes', yPosition);

        // Set normal font weight for notes content
        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(31, 41, 55);

        const actualNotesHeight = addWrappedText(
          notesText,
          margin,
          yPosition,
          contentWidth,
          12,
          1.4, // Slightly more spacing for readability
        );
        yPosition += actualNotesHeight + fieldSpacing;
      }

      // Images Section - Dynamic page breaks for images
      if (pdfData.imageUrls && pdfData.imageUrls.length > 0) {
        const imageWidth = 200;
        const imageHeight = 150;
        const imagesPerRow = 2;
        const imageSpacing = 20;
        const imageLabelHeight = 20;
        const rowHeight = imageHeight + imageLabelHeight + imageSpacing;

        // Add section header
        yPosition = addSectionHeader('Attached Images', yPosition);

        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(102, 102, 102);
        pdf.text(
          `Number of images attached: ${pdfData.imageUrls.length}`,
          margin,
          yPosition,
        );
        yPosition += 20;

        // Add images to PDF with dynamic page breaks
        let imageX = margin;
        let imageY = yPosition;

        for (let i = 0; i < pdfData.imageUrls.length; i++) {
          const imageUrl = pdfData.imageUrls[i];

          // Check if we need a new row
          if (i > 0 && i % imagesPerRow === 0) {
            imageX = margin;
            imageY += rowHeight;

            // Check if we need a new page for the next row
            if (imageY + rowHeight > pageHeight - margin) {
              pdf.addPage();
              imageY = margin;
              currentPage++;
            }
          }

          // Check if we need a new page for the current image
          if (imageY + imageHeight + imageLabelHeight > pageHeight - margin) {
            pdf.addPage();
            imageY = margin;
            imageX = margin;
            currentPage++;
          }

          // Add image label
          pdf.setFontSize(10);
          pdf.setFont('helvetica', 'bold');
          pdf.setTextColor(31, 41, 55);
          pdf.text(`Image ${i + 1}:`, imageX, imageY - 5);

          // Add image
          await addImageToPdf(
            imageUrl,
            imageX,
            imageY,
            imageWidth,
            imageHeight,
          );

          imageX += imageWidth + imageSpacing;
        }

        yPosition = imageY + imageHeight + imageLabelHeight + 40;
      }

      // Footer - Add to the last page
      const totalPages = pdf.getNumberOfPages();

      for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
        pdf.setPage(pageNum);

        const footerY = pageHeight - 60;

        pdf.setDrawColor(229, 231, 235);
        pdf.setLineWidth(1);
        pdf.line(margin, footerY, pageWidth - margin, footerY);

        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(102, 102, 102);

        const footerText1 = 'This referral was generated using VideaAI';
        const footerText2 = `Appointment Date: ${formatAppointmentDate()}`;
        const pageText = `Page ${pageNum} of ${totalPages}`;

        const footer1Width = pdf.getTextWidth(footerText1);
        const footer2Width = pdf.getTextWidth(footerText2);
        const pageTextWidth = pdf.getTextWidth(pageText);

        pdf.text(footerText1, (pageWidth - footer1Width) / 2, footerY + 20);
        pdf.text(footerText2, (pageWidth - footer2Width) / 2, footerY + 35);
        pdf.text(pageText, (pageWidth - pageTextWidth) / 2, footerY + 50);
      }

      // Save the PDF
      pdf.save(`${fileName}.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  return {
    generatePdf,
    isGenerating,
    isLoading: isLoadingPdfInfo,
    hasData: !!pdfData,
  };
};
